import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { dispatch, useSelector } from '../../../redux/store';
import {
  acceptPosDrawerTransfer,
  dismissIncomingTransferDialog,
  getPosDrawerHandoverSummary,
  getPosDrawerSessionContext,
  hidePOSCloseTab,
  hidePOSTab,
} from '../../../redux/slices/posRedux/posRedux';
import POSHandoverSummary from './POSHandoverSummary';
import { selectGlobalLocation } from '../../../redux/slices/settingsManagementRedux/locationsRedux';

export default function POSIncomingTransferDialog() {
  const { incomingTransferDialog, sessionContext, handoverSummary, requests } = useSelector((state) => state?.pos);
  const { data: locations, selectedLocation } = useSelector((state) => state?.location);
  const [valuesConfirmed, setValuesConfirmed] = useState(false);
  const [summaryScope, setSummaryScope] = useState('drawer');
  const transfer = sessionContext?.incomingPendingTransfer;

  const refreshValues = useCallback(async () => {
    // Refreshed figures replace the reviewed figures, so acknowledgement must start over.
    setValuesConfirmed(false);
    setSummaryScope('drawer');
    if (!transfer?.id) return null;
    try {
      return await dispatch(getPosDrawerHandoverSummary(transfer.id));
    } catch (_error) {
      return null;
    }
  }, [transfer?.id]);

  useEffect(() => {
    if (!incomingTransferDialog.open || !transfer?.id) return;
    setValuesConfirmed(false);
    setSummaryScope('drawer');
    refreshValues();
  }, [incomingTransferDialog.open, transfer?.id, refreshValues]);

  const handleLater = () => {
    if (!transfer || requests.acceptTransfer.loading) return;
    setValuesConfirmed(false);
    dispatch(dismissIncomingTransferDialog(transfer.id));
  };

  const handleAccept = async () => {
    if (!transfer || !valuesConfirmed || !handoverSummary) return;
    try {
      const response = await dispatch(acceptPosDrawerTransfer(transfer.id));
      setValuesConfirmed(false);

      const acceptedLocationId = response?.data?.drawer?.locationId;
      if (acceptedLocationId && Number(selectedLocation?.id) !== Number(acceptedLocationId)) {
        dispatch(hidePOSTab());
        dispatch(hidePOSCloseTab());
        const acceptedLocation = locations?.find((location) => Number(location.id) === Number(acceptedLocationId));
        if (acceptedLocation) {
          selectGlobalLocation(acceptedLocation);
        } else {
          toast.warning('The register transfer was accepted, but its location is not loaded. Select the register location or reload before using POS.');
        }
      }
    } catch (_error) {
      // Keep the handover visible so the recipient can refresh or retry safely.
    }
  };

  const refreshTransfer = async () => {
    setValuesConfirmed(false);
    try {
      const response = await dispatch(getPosDrawerSessionContext());
      if (response?.data?.incomingPendingTransfer) await refreshValues();
    } catch (_error) {
      // The independent context request state explains the failure without hiding the transfer.
    }
  };

  if (!transfer) return null;

  return (
    <Dialog open={incomingTransferDialog.open} onClose={handleLater} maxWidth={false} scroll='paper' PaperProps={{ sx: { width: '98vw', height: '98vh', maxWidth: '98vw', m: 1 } }}>
      <DialogTitle>Accept Register Transfer</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={9}>
            <POSHandoverSummary summary={handoverSummary} loading={requests.handoverSummary.loading} error={requests.handoverSummary.error?.message} scope={summaryScope} onScopeChange={setSummaryScope} onRefresh={refreshValues} />
          </Grid>
          <Grid item xs={12} lg={3}>
            <Paper variant='outlined' sx={{ p: 2, borderRadius: 3, position: { lg: 'sticky' }, top: 0 }}>
              <Stack spacing={2}>
                <Typography variant='h6'>Acceptance Details</Typography>
                {requests.acceptTransfer.error?.message && <Alert severity='error'>{requests.acceptTransfer.error.message}</Alert>}
                {requests.sessionContext.error?.message && <Alert severity='error'>{requests.sessionContext.error.message}</Alert>}
                <Stack direction='row' spacing={1.5} alignItems='center'><Avatar src={transfer.sourceSession?.user?.userImage || undefined} alt={transfer.sourceSession?.user?.name} /><Box><Typography variant='subtitle1'>{transfer.sourceSession?.user?.name || 'Current cashier'}</Typography><Typography variant='body2' color='text.secondary'>Requested {moment(transfer.createdAt).format('YYYY-MM-DD HH:mm')}</Typography></Box></Stack>
                <Paper variant='outlined' sx={{ p: 1.5 }}><Stack spacing={0.75}><Typography variant='body2'>Location: {handoverSummary?.drawer?.locationName || transfer.drawer?.locationId || 'Unavailable'}</Typography><Typography variant='body2'>Drawer ID: {transfer.posCashDrawerId}</Typography><Typography variant='body2'>Session: {transfer.sourceSession?.id} / #{transfer.sourceSession?.sequenceNo}</Typography></Stack></Paper>
                <Alert severity='warning'>The current cashier may continue trading until acceptance. Refresh the values if time has passed since they were calculated.</Alert>
                <FormControlLabel control={<Checkbox checked={valuesConfirmed} disabled={!handoverSummary || requests.handoverSummary.loading} onChange={(event) => setValuesConfirmed(event.target.checked)} />} label='I have reviewed the drawer and session values and confirm that I accept responsibility for this register.' />
                <Button onClick={refreshTransfer} disabled={requests.acceptTransfer.loading || requests.sessionContext.loading}>Refresh Transfer</Button>
                <Button color='inherit' onClick={handleLater} disabled={requests.acceptTransfer.loading}>Later</Button>
                <LoadingButton variant='contained' loading={requests.acceptTransfer.loading} disabled={!valuesConfirmed || !handoverSummary || requests.handoverSummary.loading} onClick={handleAccept}>Accept Register</LoadingButton>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
